import { m } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
// @mui
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
// hooks
import { useAuth } from 'src/hooks/useAuth';
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
// utils
import axiosInstance, { endpoints } from 'src/utils/axios';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { varHover } from 'src/components/animate';
//
import NotificationItem from './notification-item';
import NotificationSettingsModal from './notification-settings-modal';

// ----------------------------------------------------------------------

const TRIGGER_LABEL = {
  leave_application:    'Leave Request',
  leave_approval:       'Leave Approved',
  leave_rejection:      'Leave Rejected',
  leave_cancellation:   'Cancel Request',
  leave_cancel_approved:'Cancellation OK',
  leave_cancel_rejected:'Cancellation Denied',
  payroll_published:    'Salary Slip Ready',
  expense_applied:      'Expense Request',
  expense_approved:     'Expense Approved',
  expense_rejected:     'Expense Rejected',
  attendance_correction:'Attendance Update',
  document_request:     'Document Request',
  manual:               'Announcement',
};

function mapItem(item) {
  return {
    id: item._id,
    title: item.announcementName,
    subtitle: item.subject,
    message: item.message || '',
    category: TRIGGER_LABEL[item.triggerSource] || 'Announcement',
    triggerSource: item.triggerSource,
    referenceId: item.referenceId || null,
    // actionStatus: null = still pending, 'approved' = approved, 'rejected' = rejected
    actionStatus: item.actionStatus || null,
    createdAt: item.createdAt,
    type: 'announcement',
  };
}

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const { user, rights } = useAuth();
  const drawer = useBoolean();
  const settings = useBoolean();
  const smUp = useResponsive('up', 'sm');

  const [currentTab, setCurrentTab] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // id of item being acted on

  // Reject dialog state
  const [rejectDialog, setRejectDialog] = useState({ open: false, notificationId: null, leaveId: null });
  const [rejectReason, setRejectReason] = useState('');

  // Read state — persisted in localStorage
  const [readIds, setReadIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('notif_read') || '[]')); }
    catch { return new Set(); }
  });

  // Dismissed (archived) — persisted in localStorage
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('notif_dismissed') || '[]')); }
    catch { return new Set(); }
  });

  // ── Fetch ─────────────────────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(endpoints.announcement.myNotifications, {
        _skipRedirectOn403: true,
      });
      setNotifications((res.data?.data || []).map(mapItem));
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);
  useEffect(() => { if (drawer.value) fetchNotifications(); }, [drawer.value, fetchNotifications]);


  const checkRight = useCallback((module, menu, submenu, action = 'view_all') => {
    const role = user?.role;
    if (role === 'super_admin') return true;
    if (role === 'admin' && module !== 'super_admin') return true;
    if (!rights) return false;

    if (submenu) {
      return !!rights[module]?.children?.[menu]?.children?.[submenu]?.[action];
    }
    return !!rights[module]?.children?.[menu]?.[action];
  }, [user, rights]);

  const isNotificationEnabled = useCallback((notification) => {
    const expenseFeatureEnabled = user?.organizationFeatures?.expenseFeatureEnabled !== false;
    
    if (notification.triggerSource === 'expense_applied' ||
        notification.triggerSource === 'expense_approved' ||
        notification.triggerSource === 'expense_rejected') {
      if (!expenseFeatureEnabled) return false;
    }

    switch (notification.triggerSource) {
      case 'leave_application':
      case 'leave_cancellation':
        return checkRight('leave_mangement', 'leave_approvals', null, 'view_all') ||
               checkRight('leave_mangement', 'leave_approvals', null, 'view_self') ||
               checkRight('leave_mangement', 'leave_approvals', null, 'approve') ||
               checkRight('leave_mangement', 'leave_approvals', null, 'approve_self');
               
      case 'leave_approval':
      case 'leave_rejection':
      case 'leave_cancel_approved':
      case 'leave_cancel_rejected':
        return checkRight('leave_mangement', 'leave_applications', null, 'view_all') ||
               checkRight('leave_mangement', 'leave_applications', null, 'view_self');

      case 'expense_applied':
        return checkRight('expense', 'expense_approvals', null, 'view_all') ||
               checkRight('expense', 'expense_approvals', null, 'view_self') ||
               checkRight('expense', 'expense_approvals', null, 'approve') ||
               checkRight('expense', 'expense_approvals', null, 'approve_self');

      case 'expense_approved':
      case 'expense_rejected':
        return checkRight('expense', 'manage_expense', null, 'view_all') ||
               checkRight('expense', 'manage_expense', null, 'view_self');

      case 'forget_punch_applied':
        return checkRight('hrm_management', 'attendance', 'forget_punch_approval', 'view_all') ||
               checkRight('hrm_management', 'attendance', 'forget_punch_approval', 'view_self') ||
               checkRight('hrm_management', 'attendance', 'forget_punch_approval', 'approve') ||
               checkRight('hrm_management', 'attendance', 'forget_punch_approval', 'approve_self');

      case 'forget_punch_approved':
      case 'forget_punch_rejected':
        return checkRight('hrm_management', 'attendance', 'forget_punch', 'view_all') ||
               checkRight('hrm_management', 'attendance', 'forget_punch', 'view_self');

      default:
        return true;
    }
  }, [user, checkRight]);

  // ── Derived lists ─────────────────────────────────────────────────────
  const enriched = notifications.map((n) => ({
    ...n,
    isUnRead: !readIds.has(n.id),
    isArchived: dismissedIds.has(n.id),
  }));

  const enabledNotifications  = enriched.filter(isNotificationEnabled);
  const allNotifications      = enabledNotifications.filter((n) => !n.isArchived);
  const unreadNotifications   = allNotifications.filter((n) => n.isUnRead);
  const archivedNotifications = enabledNotifications.filter((n) => n.isArchived || !n.isUnRead);
  const totalUnRead           = unreadNotifications.length;

  let filteredList;
  if (currentTab === 'unread')    filteredList = unreadNotifications;
  else if (currentTab === 'archived') filteredList = archivedNotifications;
  else filteredList = allNotifications;

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleChangeTab = useCallback((_, v) => setCurrentTab(v), []);

  const markRead = useCallback((id) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      localStorage.setItem('notif_read', JSON.stringify([...next]));
      return next;
    });
  }, []);

  const handleMarkAllAsRead = () => {
    const allIds = new Set(notifications.map((n) => n.id));
    setReadIds(allIds);
    localStorage.setItem('notif_read', JSON.stringify([...allIds]));
  };

  const handleArchive = useCallback((id) => {
    markRead(id);
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      localStorage.setItem('notif_dismissed', JSON.stringify([...next]));
      return next;
    });
  }, [markRead]);

  // ── Leave actions ─────────────────────────────────────────────────────
  const handleAccept = useCallback(async (notification) => {
    if (!notification.referenceId) return;
    setActionLoading(notification.id);
    markRead(notification.id);
    try {
      await axiosInstance.patch(endpoints.leaveApplication.approve(notification.referenceId), {}, {
        _skipRedirectOn403: true,
      });
      // Sync actionStatus in backend so bell reflects this across page refreshes
      await axiosInstance.patch(endpoints.announcement.updateActionStatus, {
        referenceId: notification.referenceId,
        actionStatus: 'approved',
      }).catch(() => {}); // fire-and-forget — don't break on error
      handleArchive(notification.id);
      fetchNotifications();
    } catch (err) {
      console.error('Failed to approve leave:', err);
    } finally {
      setActionLoading(null);
    }
  }, [markRead, handleArchive, fetchNotifications]);

  const openRejectDialog = useCallback((notification) => {
    setRejectDialog({ open: true, notificationId: notification.id, leaveId: notification.referenceId });
    setRejectReason('');
    markRead(notification.id);
  }, [markRead]);

  const handleRejectConfirm = async () => {
    const { notificationId, leaveId } = rejectDialog;
    if (!leaveId) return;
    setActionLoading(notificationId);
    try {
      await axiosInstance.patch(endpoints.leaveApplication.reject(leaveId), { rejectionReason: rejectReason }, {
        _skipRedirectOn403: true,
      });
      // Sync actionStatus in backend
      await axiosInstance.patch(endpoints.announcement.updateActionStatus, {
        referenceId: leaveId,
        actionStatus: 'rejected',
      }).catch(() => {});
      handleArchive(notificationId);
      fetchNotifications();
    } catch (err) {
      console.error('Failed to reject leave:', err);
    } finally {
      setActionLoading(null);
      setRejectDialog({ open: false, notificationId: null, leaveId: null });
    }
  };

  // ── Tabs ──────────────────────────────────────────────────────────────
  const TABS = [
    { value: 'all',      label: 'All',      count: allNotifications.length,      color: 'default' },
    { value: 'unread',   label: 'Unread',   count: unreadNotifications.length,   color: 'info' },
    { value: 'archived', label: 'Archived', count: archivedNotifications.length, color: 'success' },
  ];

  // ── Render head ───────────────────────────────────────────────────────
  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!totalUnRead && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllAsRead} size="small">
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Refresh">
        <IconButton onClick={fetchNotifications} disabled={loading} size="small">
          <Iconify icon={loading ? 'svg-spinners:ring-resize' : 'eva:refresh-fill'} />
        </IconButton>
      </Tooltip>

      {!smUp && (
        <IconButton onClick={drawer.onFalse} size="small">
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  // ── Render tabs ───────────────────────────────────────────────────────
  const renderTabs = (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pl: 2.5, pr: 1 }}>
      <Tabs value={currentTab} onChange={handleChangeTab}>
        {TABS.map((tab) => (
          <Tab
            key={tab.value}
            iconPosition="end"
            value={tab.value}
            label={tab.label}
            icon={
              <Label variant={(tab.value === currentTab && 'filled') || 'soft'} color={tab.color}>
                {tab.count}
              </Label>
            }
            sx={{ '&:not(:last-of-type)': { mr: 3 }, minHeight: 48 }}
          />
        ))}
      </Tabs>
      <Tooltip title="Notification Settings">
        <IconButton onClick={settings.onTrue} size="small">
          <Iconify icon="solar:settings-bold-duotone" />
        </IconButton>
      </Tooltip>
    </Stack>

  );

  // ── List content ──────────────────────────────────────────────────────
  let listContent;
  if (loading) {
    listContent = (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
        <CircularProgress size={32} color="primary" />
        <Typography variant="caption" color="text.disabled" sx={{ mt: 1.5 }}>
          Loading notifications…
        </Typography>
      </Stack>
    );
  } else if (filteredList.length === 0) {
    listContent = (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
        <Iconify icon="solar:bell-off-bold-duotone" width={48} sx={{ color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.disabled">
          {currentTab === 'archived' ? 'No archived notifications' : 'No notifications'}
        </Typography>
      </Stack>
    );
  } else {
    listContent = (
      <List disablePadding>
        {filteredList.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            actionLoading={actionLoading === notification.id}
            onMarkRead={markRead}
            onArchive={handleArchive}
            onAccept={handleAccept}
            onReject={openRejectDialog}
          />
        ))}
      </List>
    );
  }

  const renderList = <Scrollbar sx={{ height: 1 }}>{listContent}</Scrollbar>;

  // ── Reject dialog ─────────────────────────────────────────────────────
  const renderRejectDialog = (
    <Dialog
      open={rejectDialog.open}
      onClose={() => setRejectDialog({ open: false, notificationId: null, leaveId: null })}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Reject Leave Request</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          rows={3}
          label="Rejection Reason (optional)"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setRejectDialog({ open: false, notificationId: null, leaveId: null })}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleRejectConfirm}
          variant="contained"
          color="error"
          disabled={actionLoading !== null}
        >
          Reject
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ── Main ──────────────────────────────────────────────────────────────
  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={drawer.onTrue}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420, display: 'flex', flexDirection: 'column' },
        }}
      >
        {renderHead}
        <Divider />
        {renderTabs}
        <Divider />
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {renderList}
        </Box>
        <Box sx={{ p: 1 }}>
          <Button fullWidth size="large" onClick={fetchNotifications} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Box>
      </Drawer>

      {renderRejectDialog}

      <NotificationSettingsModal open={settings.value} onClose={settings.onFalse} />
    </>
  );
}
